import {
  cn,
  Pill,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  OccurrenceSource,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
  RecordSupportClass,
  RecordSupportClassTooltip,
} from '@/api/rest/resources/types/fact';
import {
  MapRadiusContext,
  NO_RADIUS,
  useMapRadiusContext,
} from '@/components/MapOverview/components/MapRadiusContextProvider';
import { NO_DATA_PLACEHOLDER } from '@/config/constants';
import { getLabelValueFromFact } from '@/pages/shared/hooks/utils';

type SpeciesRecord = {
  species: string | undefined;
  distance: number | undefined;
  value: number;
  firstYear: string | undefined;
  lastYear: string | undefined;
  source: OccurrenceSource | undefined;
  supportClass: RecordSupportClass | undefined;
  supportCode: RecordSupportClassTooltip | undefined;
};

export const InvasiveSpeciesTable = ({
  observedSpecies,
  registeredSpecies,
}: {
  observedSpecies: PlotReportFactElement[];
  registeredSpecies: PlotReportFactElement[];
}) => {
  const { t } = useTranslation();
  const hasRadiusContext = !!useContext(MapRadiusContext);

  const formattedData = [
    ...getObservedSpeciesData(observedSpecies),
    ...getRegisteredMissingSpeciesData(registeredSpecies),
  ] as Partial<SpeciesRecord>[];

  const columns = [
    {
      accessorKey: 'species',
      header: t('shared.ncaDetail.details.invasiveSpecies.table.species'),
    },
    {
      accessorKey: 'distance',
      header: `${t('shared.ncaDetail.details.invasiveSpecies.table.distance')} (${t('global.ui.unit.km')})`,
    },
    {
      accessorKey: 'value',
      header: t('shared.ncaDetail.details.invasiveSpecies.table.value'),
    },
    {
      accessorKey: 'source',
      header: t('shared.ncaDetail.details.invasiveSpecies.table.source'),
    },
  ];

  if (hasRadiusContext) {
    columns.push({
      accessorKey: 'confidence',
      header: t('shared.ncaDetail.details.invasiveSpecies.table.confidence'),
    });
  }

  return (
    <div className='w-full [&>div]:overflow-x-hidden'>
      <Table data-testid='invasive-species-table' className='w-full'>
        <TableHeader className='block'>
          <TableRow className='table w-full table-fixed'>
            {columns.map((column) => (
              <TableHead key={column.accessorKey} className='text-center'>
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody className='block max-h-[300px] overflow-y-auto [scrollbar-gutter:stable]'>
          {formattedData.map((row, index) => {
            const hasDistanceValue = typeof row?.distance === 'number';
            const isInPlot = hasDistanceValue && row.distance === 0;
            return (
              <TableRow
                key={`species-register-${index}`}
                className={cn(
                  'table w-full table-fixed',
                  index === 0 && hasDistanceValue && 'typography-body2Semibold',
                  isInPlot && 'typography-body2Semibold',
                )}
              >
                <Cell className='italic typography-body2' value={row?.species} />
                <Cell value={isInPlot ? t('shared.ncaDetail.details.invasiveSpecies.table.inPlot') : row?.distance} />
                <ObservationCell value={row?.value} firstYear={row?.firstYear} lastYear={row?.lastYear} />
                <SourcePill source={row?.source} />
                {hasRadiusContext && <ConfidencePill supportClass={row?.supportClass} supportCode={row?.supportCode} />}
              </TableRow>
            );
          })}
          {formattedData.length === 0 && (
            <TableRow className='table w-full table-fixed'>
              <td colSpan={columns.length} className='text-center typography-body2 text-text-secondary py-4'>
                {t('shared.ncaDetail.details.invasiveSpecies.table.noData')}
              </td>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

const Cell = ({ value, className }: { value: string | number | undefined; className?: string }) => {
  return (
    <TableCell className={cn('text-center typography-body2', className)}>{value ?? NO_DATA_PLACEHOLDER}</TableCell>
  );
};

export const ObservationCell = ({
  value,
  firstYear,
  lastYear,
}: {
  value: number | undefined;
  firstYear: string | undefined;
  lastYear: string | undefined;
}) => {
  const { t } = useTranslation();

  return (
    <TableCell className='text-center typography-body2'>
      <span className='inline-flex items-center justify-center gap-1'>
        {value ?? NO_DATA_PLACEHOLDER}
        {value !== undefined && (
          <Tooltip>
            <TooltipTrigger asChild>
              <i data-testid='observation-info-icon' className='ri-information-line text-active-54' />
            </TooltipTrigger>
            <TooltipContent className='text-left'>
              <Stack spacing={2}>
                <span className='typography-body2Semibold'>
                  {t('shared.ncaDetail.details.invasiveSpecies.table.samplingPeriod')}
                </span>
                <span className='typography-body2'>
                  {t('shared.ncaDetail.details.invasiveSpecies.table.firstObservation', {
                    year: firstYear ?? NO_DATA_PLACEHOLDER,
                  })}
                </span>
                <span className='typography-body2'>
                  {t('shared.ncaDetail.details.invasiveSpecies.table.lastObservation', {
                    year: lastYear ?? NO_DATA_PLACEHOLDER,
                  })}
                </span>
              </Stack>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        )}
      </span>
    </TableCell>
  );
};

const SourcePill = ({ source }: { source: OccurrenceSource | undefined }) => {
  const { t } = useTranslation();

  if (!source || source === OccurrenceSource.notObserved) {
    return (
      <Cell
        className='text-text-disabled'
        value={source ? t('shared.ncaDetail.details.invasiveSpecies.sources.notObserved') : NO_DATA_PLACEHOLDER}
      />
    );
  }

  const styles = getSourceStyles(source);

  return (
    <TableCell>
      <Pill className='px-2 py-1 typography-body2Semibold' style={styles}>
        {t(`shared.ncaDetail.details.invasiveSpecies.sources.${source}`)}
      </Pill>
    </TableCell>
  );
};

export const ConfidencePill = ({
  supportClass,
  supportCode,
}: {
  supportClass: RecordSupportClass | undefined;
  supportCode: RecordSupportClassTooltip | undefined;
}) => {
  const { t } = useTranslation();
  const { radius } = useMapRadiusContext();
  const supportedRadius = [NO_RADIUS, '30'].includes(radius);

  if (!supportedRadius) {
    return (
      <TableCell className='text-center'>
        <Tooltip>
          <TooltipTrigger asChild>
            <i className='ri-information-line text-active-54' />
          </TooltipTrigger>
          <TooltipContent className='text-left'>
            <Stack spacing={2}>
              <span className='typography-body2'>
                {t('shared.ncaDetail.details.invasiveSpecies.labels.limitedRadiusFeature')}
              </span>
            </Stack>
            <TooltipArrow />
          </TooltipContent>
        </Tooltip>
      </TableCell>
    );
  }

  if (!supportClass || supportClass === RecordSupportClass.unknown) {
    return <Cell className='text-text-disabled' value={NO_DATA_PLACEHOLDER} />;
  }

  const styles = getConfidenceStyles(supportClass);

  return (
    <TableCell className='text-center'>
      <Tooltip>
        <TooltipTrigger asChild>
          <Pill className='px-2 py-1 typography-body2Semibold' style={styles}>
            {t(`shared.ncaDetail.details.invasiveSpecies.recordSupportClass.categories.${supportClass}`)}
          </Pill>
        </TooltipTrigger>
        <TooltipContent className='text-left'>
          <Stack spacing={2}>
            <span className='typography-body2Semibold'>
              {t('shared.ncaDetail.details.invasiveSpecies.recordSupportTitle')}
            </span>
            {supportClass && (
              <span className='typography-body2'>
                {t(`shared.ncaDetail.details.invasiveSpecies.recordSupportClass.descriptions.${supportClass}`)}
              </span>
            )}
            {supportCode && (
              <span className='typography-body2'>
                {t(`shared.ncaDetail.details.invasiveSpecies.recordSupportCode.${supportCode}`)}
              </span>
            )}
          </Stack>
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    </TableCell>
  );
};

const getConfidenceStyles = (supportClass: RecordSupportClass) => {
  switch (supportClass) {
    case RecordSupportClass.strong:
      return { color: '#2f6b3a', background: '#d8f0dc' };
    case RecordSupportClass.localized:
      return { color: '#4f6b2a', background: '#e6f0d2' };
    case RecordSupportClass.moderate:
      return { color: '#8a6a12', background: '#fbf0c9' };
    case RecordSupportClass.narrow:
      return { color: '#9b5a1f', background: '#fbe3cc' };
    case RecordSupportClass.limited:
      return { color: '#9b3b2f', background: '#f9dcd7' };
    case RecordSupportClass.unknown:
    default:
      return { color: '#5f6670', background: '#e8eaed' };
  }
};

const getSourceStyles = (source: OccurrenceSource) => {
  switch (source) {
    case OccurrenceSource.inSitu:
      return {
        color: '#627e58',
        background: '#e1eeda',
      };
    case OccurrenceSource.gbif:
    default:
      return {
        color: '#586c9b',
        background: '#e0e8fa',
      };
  }
};

const getObservedSpeciesData = (observedSpecies: PlotReportFactElement[]): SpeciesRecord[] => {
  return observedSpecies
    .flatMap((f) => {
      if (typeof f.value !== 'number') {
        return [];
      }

      if (f.labels?.length === 0) {
        return [];
      }

      return {
        species: getLabelValueFromFact(f, PlotReportFactLabelNameEnum.species) as string | undefined,
        distance: getLabelValueFromFact(f, PlotReportFactLabelNameEnum.closest_distance_to_polygon_km) as
          | number
          | undefined,
        value: f.value,
        firstYear: getLabelValueFromFact(f, PlotReportFactLabelNameEnum.first_record_year) as string | undefined,
        lastYear: getLabelValueFromFact(f, PlotReportFactLabelNameEnum.last_record_year) as string | undefined,
        source: getLabelValueFromFact(f, PlotReportFactLabelNameEnum.occurrence_source) as OccurrenceSource | undefined,
        supportClass: getLabelValueFromFact(f, PlotReportFactLabelNameEnum.record_support_class) as
          | RecordSupportClass
          | undefined,
        supportCode: getLabelValueFromFact(f, PlotReportFactLabelNameEnum.record_support_tooltip_code) as
          | RecordSupportClassTooltip
          | undefined,
      };
    })
    .sort((a, b) => (a?.distance ?? 0) - (b?.distance ?? 0));
};

const getRegisteredMissingSpeciesData = (registeredSpecies: PlotReportFactElement[]): Partial<SpeciesRecord>[] => {
  return registeredSpecies.flatMap((f) => {
    if (!(typeof f.value === 'number' && f.value === 0)) {
      return [];
    }

    if (f.labels?.length === 0) {
      return [];
    }

    return {
      species: getLabelValueFromFact(f, PlotReportFactLabelNameEnum.species) as string | undefined,
      source: OccurrenceSource.notObserved,
    } as Partial<SpeciesRecord>;
  });
};
